import { Link, useNavigate } from "react-router-dom";
import { useState, useMemo, useCallback } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BranchRegionFilter, type BranchRegionState } from "@/components/BranchRegionFilter";
import { useAppData } from "@/data/AppDataContext";
import { useDataIsolation } from "@/hooks/useDataIsolation";
import { formatAddress } from "@/lib/addressUtils";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";
import {
  CockpitPage,
  CockpitMasthead,
  HeroMetric,
  HeroDivider,
  CockpitFilterBar,
  CockpitSearch,
  CockpitChipGroup,
  CockpitListShell,
  CockpitColumns,
  CockpitRow,
  CockpitEmpty,
  InlineMeta,
  StatusDot,
} from "@/components/cockpit/CockpitShell";

interface CustomerSummary {
  customerId?: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  branch: string;
  region: string;
  jobCount: number;
  openJobCount: number;
  totalValue: number;
  openIssueCount: number;
  lastActivity: string;
  hasActiveJobs: boolean;
  hasCompletedOnly: boolean;
}

type SortField = "jobCount" | "totalValue" | "openIssueCount" | "lastActivity";
type SortDir = "asc" | "desc";

const TEMPLATE = "minmax(220px,1.3fr) minmax(150px,.8fr) 90px 90px 120px 110px 24px";

export default function CustomersListCockpit() {
  const { issues } = useAppData();
  const { jobs } = useDataIsolation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeView, setActiveView] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [brFilter, setBrFilter] = useState<BranchRegionState>({ branch: "all", region: "all" });
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const customers = useMemo(() => {
    const map = new Map<string, CustomerSummary>();
    for (const job of jobs) {
      const customerId = String((job as { customerId?: string }).customerId ?? "").trim() || undefined;
      const mapKey = customerId || job.customerName;
      const existing = map.get(mapKey);
      const city = job.address.city || "";
      const isOpen = job.currentStage !== "Completed";
      const jobIssues = issues.filter((i) => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"));
      const lastAct = job.activityHistory.length > 0 ? job.activityHistory[job.activityHistory.length - 1].date : job.scheduledInstallDate;

      if (existing) {
        existing.jobCount += 1;
        existing.openJobCount += isOpen ? 1 : 0;
        existing.totalValue += getJobDisplayValue(job);
        existing.openIssueCount += jobIssues.length;
        if (lastAct > existing.lastActivity) existing.lastActivity = lastAct;
        if (!existing.city && city) existing.city = city;
        if (isOpen) existing.hasActiveJobs = true;
        if (!isOpen && !existing.hasActiveJobs) existing.hasCompletedOnly = true;
      } else {
        map.set(mapKey, {
          customerId,
          name: job.customerName,
          phone: job.phone,
          email: job.email || "",
          address: formatAddress(job.address),
          city,
          branch: job.branch,
          region: job.region,
          jobCount: 1,
          openJobCount: isOpen ? 1 : 0,
          totalValue: getJobDisplayValue(job),
          openIssueCount: jobIssues.length,
          lastActivity: lastAct,
          hasActiveJobs: isOpen,
          hasCompletedOnly: !isOpen,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.lastActivity.localeCompare(a.lastActivity));
  }, [jobs, issues]);

  const cities = useMemo(() => Array.from(new Set(customers.map((c) => c.city).filter(Boolean))).sort(), [customers]);
  const recentCutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  }, []);

  const counts = useMemo(() => ({
    withIssues: customers.filter((c) => c.openIssueCount > 0).length,
    withOpenJobs: customers.filter((c) => c.openJobCount > 0).length,
    recentActivity: customers.filter((c) => c.lastActivity >= recentCutoff).length,
    completedOnly: customers.filter((c) => c.hasCompletedOnly && !c.hasActiveJobs).length,
    totalOpen: customers.reduce((s, c) => s + c.openJobCount, 0),
    totalIssues: customers.reduce((s, c) => s + c.openIssueCount, 0),
    totalValue: customers.reduce((s, c) => s + c.totalValue, 0),
  }), [customers, recentCutoff]);

  const filtered = useMemo(() => {
    let list = customers;
    if (activeView === "active") list = list.filter((c) => c.openJobCount > 0);
    else if (activeView === "issues") list = list.filter((c) => c.openIssueCount > 0);
    else if (activeView === "recent") list = list.filter((c) => c.lastActivity >= recentCutoff);
    else if (activeView === "completed") list = list.filter((c) => c.hasCompletedOnly && !c.hasActiveJobs);
    if (cityFilter !== "all") list = list.filter((c) => c.city === cityFilter);
    if (brFilter.branch !== "all") list = list.filter((c) => c.branch === brFilter.branch);
    if (brFilter.region !== "all") list = list.filter((c) => c.region === brFilter.region);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c) =>
        c.name.toLowerCase().includes(q) ||
        c.city.toLowerCase().includes(q) ||
        c.phone.includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.address.toLowerCase().includes(q) ||
        String(c.customerId ?? "").toLowerCase().includes(q),
      );
    }
    if (sortField) {
      list = [...list].sort((a, b) => {
        const cmp = sortField === "lastActivity" ? a.lastActivity.localeCompare(b.lastActivity) : a[sortField] - b[sortField];
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return list;
  }, [customers, activeView, cityFilter, brFilter, search, recentCutoff, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safeP = Math.min(page, totalPages);
  const paginated = filtered.slice((safeP - 1) * pageSize, safeP * pageSize);

  const setView = (v: string) => { setActiveView(v); setPage(1); };
  const setSearchReset = (v: string) => { setSearch(v); setPage(1); };
  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortField(field); setSortDir("desc"); }
  };

  const exportCSV = useCallback(() => {
    const headers = ["Name", "Phone", "Email", "Address", "City", "Branch", "Region", "Jobs", "Open Jobs", "Open Issues", "Total Value", "Last Activity"];
    const rows = filtered.map((c) => [c.name, c.phone, c.email, c.address, c.city, c.branch, c.region, c.jobCount, c.openJobCount, c.openIssueCount, c.totalValue, c.lastActivity]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  return (
    <CockpitPage>
      <CockpitMasthead
        title="Customers"
        right={(
          <>
            <HeroMetric label="Customers" value={customers.length} hero />
            <HeroDivider />
            <HeroMetric label="Open jobs" value={counts.totalOpen} />
            <HeroMetric label="Open issues" value={counts.totalIssues} tone={counts.totalIssues > 0 ? "bad" : "default"} />
            <HeroMetric label="Total value" value={`$${counts.totalValue.toLocaleString()}`} />
          </>
        )}
      />

      <CockpitFilterBar
        search={<CockpitSearch value={search} onChange={setSearchReset} placeholder="Search customers" />}
        chips={(
          <CockpitChipGroup
            value={activeView}
            onChange={setView}
            options={[
              { value: "all", label: "All", count: customers.length },
              { value: "active", label: "Active", count: counts.withOpenJobs },
              { value: "issues", label: "Issues", count: counts.withIssues },
              { value: "recent", label: "Recent", count: counts.recentActivity },
              { value: "completed", label: "Closed", count: counts.completedOnly },
            ]}
          />
        )}
        right={(
          <>
            <Select value={cityFilter} onValueChange={(v) => { setCityFilter(v); setPage(1); }}>
              <SelectTrigger className="h-9 text-xs w-[150px] bg-transparent border-border/60">
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <BranchRegionFilter value={brFilter} onChange={(v) => { setBrFilter(v); setPage(1); }} jobs={jobs} />
            <Button variant="outline" size="sm" className="h-9 text-xs" onClick={exportCSV}>
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export
            </Button>
          </>
        )}
      />

      <CockpitListShell>
        <CockpitColumns template={TEMPLATE}>
          <button className="text-left" onClick={() => toggleSort("lastActivity")}>Customer</button>
          <span>Contact</span>
          <button className="text-left" onClick={() => toggleSort("jobCount")}>Jobs</button>
          <button className="text-left" onClick={() => toggleSort("openIssueCount")}>Issues</button>
          <button className="text-left" onClick={() => toggleSort("totalValue")}>Value</button>
          <span>Updated</span>
          <span />
        </CockpitColumns>

        {paginated.length === 0 ? (
          <button type="button" onClick={() => navigate("/opportunities")} className="block w-full text-left">
            <CockpitEmpty message="No customers match this view." />
          </button>
        ) : paginated.map((c) => (
          <CockpitRow
            key={c.customerId || c.name}
            template={TEMPLATE}
            to={c.customerId ? `/customers/${c.customerId}` : `/customers/${encodeURIComponent(c.name)}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <StatusDot tone={c.openIssueCount > 0 ? "bad" : c.openJobCount > 0 ? "good" : "muted"} />
                <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
              </div>
              <InlineMeta items={[c.address || c.city || "Florida"]} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-foreground tabular-nums truncate">{c.phone}</p>
              <p className="text-xs text-muted-foreground truncate">{c.email || "No email"}</p>
            </div>
            <p className="text-sm text-foreground tabular-nums">{c.jobCount} <span className="text-xs text-muted-foreground">total</span></p>
            <p className="text-sm text-foreground tabular-nums">{c.openIssueCount || "—"}</p>
            <p className="text-sm text-foreground tabular-nums">${c.totalValue.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground tabular-nums">{c.lastActivity}</p>
          </CockpitRow>
        ))}
      </CockpitListShell>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border/60 pt-4">
          <p className="text-xs text-muted-foreground tabular-nums">
            Showing {(safeP - 1) * pageSize + 1}–{Math.min(safeP * pageSize, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" disabled={safeP <= 1} onClick={() => setPage(safeP - 1)}>Previous</Button>
            <span className="text-xs text-muted-foreground tabular-nums">{safeP} / {totalPages}</span>
            <Button variant="outline" size="sm" className="h-8 text-xs" disabled={safeP >= totalPages} onClick={() => setPage(safeP + 1)}>Next</Button>
          </div>
        </div>
      )}
    </CockpitPage>
  );
}
